import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core';
import { mapProps } from 'recompose';
import { compose } from 'react-apollo';
import { withTranslation } from 'react-i18next';
import { withRouter } from 'react-router';
import DeleteIcon from '@material-ui/icons/Delete';
import Edit from '@material-ui/icons/Edit';
// import InsertDriveIcon from '@material-ui/icons/InsertDriveFile';

import ConfirmationModal from 'components/threepp/ConfirmationModal/ConfirmationModal';
import { htmlToReact } from 'utils/functions';
import { CommentInput } from './CommentInput';
import CommentHeader from './CommentHeader';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    alignItems: 'flex-start'
  },
  actions: {
    display: 'flex',
    color: '#616161',
    cursor: 'pointer',
    transition: 'opacity 200ms linear'
  },
  showActions: {
    opacity: 1
  },
  hideActions: {
    opacity: 0
  },
  commentActionIcon: {
    width: '2rem',
    height: '2rem',
    display: 'grid',
    justifyContent: 'center',
    alignContent: 'center',
    alignItems: 'center',
    borderRadius: '50%',
    transition: 'all 0.5s ease',
    '&:hover': {
      backgroundColor: theme.palette.icon.iconHoverColor,
      cursor: 'pointer'
    },
    '&:focus': {
      outline: 'none',
      background: theme.palette.icon.iconHoverColor,
      transition: 'all 0.5s ease'
    }
  },
  actionIcon: {
    height: '20px',
    width: '20px'
  },
  commentText: {
    margin: '0.5rem 2rem 0 0',
    fontSize: '14px',
    color: theme.palette.textColor,
    '& p': {
      marginBottom: '0.75rem'
    }
  },
  fileIcon: {
    color: '#616161',
    marginRight: theme.whitespace.least,
    fontSize: '1.125rem'
  },
  attachment: {
    display: 'inline-block',
    textDecoration: 'underline',
    marginRight: theme.whitespace.base,
    overflowWrap: 'break-word'
  },
  editCommentText: {
    marginRight: theme.whitespace.base
  }
}));

export const CommentBase = props => {
  const [showActions, setShowActions] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
  const { comment, translate, sourceFieldId, commentMetadata, updateCommentsState, removeCommentMethod, currentUser, isAssessmentReadOnly } = props;
  const classes = useStyles();

  const onDeleteHandler = () => {
    setShowActions(false);
    setShowConfirmDeleteModal(true);
  };

  const onEditHandler = () => {
    setShowActions(false);
    setEditMode(true);
  };

  const onKeyPressDelete = e => {
    if (e.key === 'Enter') {
      onDeleteHandler();
    }
  };

  const onKeyPressEdit = e => {
    if (e.key === 'Enter') {
      onEditHandler();
    }
  };

  const onCloseModal = () => setShowConfirmDeleteModal(false);

  const onConfirmDelete = () => {
    removeCommentMethod(comment);
    setShowConfirmDeleteModal(false);
  };

  const isActionsVisible = !comment?.is_read_only && currentUser.email === comment.comment_user.email && !isAssessmentReadOnly;

  const onCancel = () => setEditMode(false);

  const commentBox = (
    <>
      <div className={classes.commentText}>{htmlToReact(comment.comment_text)}</div>
      {/* {comment?.attachments?.map(attachment => (
        <div key={attachment.filename} className={classes.attachment}>
          <InsertDriveIcon className={classes.fileIcon} />
          {attachment.filename}
        </div>
      ))} */}
    </>
  );

  const editCommentBox = (
    <div className={classes.editCommentText}>
      <CommentInput
        editMode={editMode}
        comment={comment}
        onCancel={onCancel}
        commentMetadata={commentMetadata}
        sourceFieldId={sourceFieldId}
        updateCommentsState={updateCommentsState}
        isAssessmentReadOnly={isAssessmentReadOnly}
      />
    </div>
  );

  const actions = isActionsVisible && (
    <div className={`${classes.actions} ${showActions ? classes.showActions : classes.hideActions}`}>
      <div className={classes.commentActionIcon} tabIndex={0} role="button" onKeyPress={onKeyPressDelete}>
        <DeleteIcon className={classes.actionIcon} onClick={onDeleteHandler} />
      </div>
      <div className={classes.commentActionIcon} tabIndex={0} role="button" onKeyPress={onKeyPressEdit}>
        <Edit className={classes.actionIcon} onClick={onEditHandler} />
      </div>
    </div>
  );

  return (
    <div
      onFocus={() => setShowActions(!editMode)}
      onMouseOver={() => setShowActions(!editMode)}
      onMouseLeave={() => setShowActions(false)}
      className={classes.root}
    >
      <ConfirmationModal
        title={translate('comments.deleteComment.title')}
        message={translate('comments.deleteComment.prompt')}
        cancelButtonText={translate('common.cancel')}
        confirmButtonText={translate('common.delete')}
        isOpen={showConfirmDeleteModal}
        onConfirm={onConfirmDelete}
        onCancel={onCloseModal}
      />
      <CommentHeader actions={actions} commentUser={comment.comment_user} lastUpdated={comment.last_updated}>
        {editMode && editCommentBox}
        {!editMode && commentBox}
      </CommentHeader>
    </div>
  );
};

export const CommentHOC = compose(
  withRouter,
  withTranslation(),
  mapProps(props => ({
    translate: props.t,
    history: props.history,
    comment: props.comment,
    commentMetadata: props.commentMetadata,
    sourceFieldId: props.sourceFieldId,
    updateCommentsState: props.updateCommentsState,
    removeCommentMethod: props.removeCommentMethod,
    currentUser: props.currentUser,
    isAssessmentReadOnly: props.isAssessmentReadOnly
  }))
);

export const Comment = CommentHOC(CommentBase);
